import { useState } from "react";
import { Search, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { MaskedLines, FadeIn, Reveal } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";

const STAGES = [
  { id: "new", label: "Received" },
  { id: "in_review", label: "In Review" },
  { id: "replied", label: "Replied" },
  { id: "quoted", label: "Quoted" },
];

const Track = () => {
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!reference.trim() || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Enter your reference number and a valid email");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/track", { reference: reference.trim(), email: email.trim() });
      setResult(r.data);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || "Something went wrong. Please try again.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const isArchived = result?.status === "archived";
  const activeIdx = result ? STAGES.findIndex((s) => s.id === result.status) : -1;

  return (
    <div data-testid="track-page">
      <PageMeta
        title="Track Inquiry | Pickleballgearz"
        description="Track the status of your pickleball manufacturing quote or RFQ in real-time."
        canonicalUrl="http://pickleballgearz.com/track"
      />
      <section className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-16 pt-16 md:pt-24 lg:px-10">
          <FadeIn>
            <p className="mono-caption mb-8 text-neutral-500">Buyer Tracking</p>
          </FadeIn>
          <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["TRACK YOUR", <>INQUIRY<span className="text-brand-red">.</span></>]} />
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[900px] px-5 py-20 md:py-28 lg:px-10">
        <Reveal>
          <form onSubmit={submit} className="grid gap-4 border border-black/10 p-6 sm:grid-cols-[1fr_1fr_auto] sm:p-8" data-testid="track-form">
            <div>
              <label className="label-pp" htmlFor="tr-ref">Reference Number</label>
              <input
                id="tr-ref"
                value={reference}
                onChange={(e) => setReference(e.target.value.toUpperCase())}
                placeholder="PP-XXXXXXXX"
                className="input-pp font-mono uppercase"
                data-testid="track-ref-input"
              />
            </div>
            <div>
              <label className="label-pp" htmlFor="tr-email">Email Used in Inquiry</label>
              <input
                id="tr-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="input-pp"
                data-testid="track-email-input"
              />
            </div>
            <div className="flex items-end">
              <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto" data-testid="track-submit-btn">
                <Search size={15} /> {loading ? "Checking..." : "Track"}
              </button>
            </div>
          </form>
        </Reveal>

        {result && (
          <Reveal className="mt-10">
            <div className="border border-brand-ink" data-testid="track-result">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-black/10 p-6">
                <div>
                  <p className="mono-caption text-neutral-400">Inquiry</p>
                  <p className="font-display text-2xl font-semibold tracking-tight" data-testid="track-reference">
                    {result.reference}
                  </p>
                </div>
                <p className="font-mono text-xs text-neutral-400">
                  Submitted {new Date(result.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className="p-6">
                {isArchived ? (
                  <div className="flex items-center gap-3 border border-black/10 bg-brand-smoke p-4" data-testid="track-status">
                    <CheckCircle2 size={20} className="text-brand-red" />
                    <p className="font-medium">This inquiry has been completed and closed. Thank you for working with us.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4" data-testid="track-status">
                    {STAGES.map((s, i) => (
                      <div key={s.id} className="relative text-center">
                        {i > 0 && (
                          <span className={`absolute left-0 right-1/2 top-3 h-px -translate-x-1/2 ${i <= activeIdx ? "bg-brand-red" : "bg-black/15"}`} style={{ left: "-50%", right: "50%" }} />
                        )}
                        <span
                          className={`relative mx-auto flex h-6 w-6 items-center justify-center border ${
                            i <= activeIdx ? "border-brand-red bg-brand-red text-white" : "border-black/20 bg-white text-neutral-300"
                          }`}
                        >
                          <span className="font-mono text-[9px]">{i + 1}</span>
                        </span>
                        <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.2em] ${i <= activeIdx ? "text-brand-ink" : "text-neutral-300"}`}>
                          {s.label}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {result.items?.length > 0 && (
                  <div className="mt-8">
                    <p className="mono-caption text-neutral-400">Items</p>
                    <div className="mt-2 divide-y divide-black/10 border-y border-black/10 font-mono text-sm">
                      {result.items.map((it, i) => (
                        <p key={i} className="py-2.5">
                          <span className="font-bold">{it.product_name}</span> — Qty {it.quantity}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {result.quote && (
                  <div className="mt-8 border border-black/10 bg-brand-smoke p-5" data-testid="track-quote">
                    <p className="mono-caption flex items-center gap-2 text-neutral-500">
                      <FileText size={13} className="text-brand-red" /> Your Quotation
                    </p>
                    <div className="mt-3 space-y-1 font-mono text-xs">
                      {result.quote.items.map((it, i) => (
                        <p key={i}>
                          {it.product_name} — {it.quantity} × {result.quote.currency} {Number(it.unit_price).toFixed(2)}
                        </p>
                      ))}
                      <p className="pt-2 text-base font-bold text-brand-ink">
                        Subtotal: {result.quote.currency} {Number(result.quote.subtotal).toFixed(2)}
                      </p>
                      <p className="pt-1 text-[10px] uppercase tracking-wider text-neutral-400">
                        Valid for {result.quote.valid_days} days from {new Date(result.quote.sent_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        )}
      </section>
    </div>
  );
};

export default Track;
